import React, { useEffect, useMemo, useState } from "react";
import Input from "components/atoms/Input";
import Button from "components/atoms/Button";
import Checkbox from "components/atoms/Checkbox";
import Link from "components/atoms/Link";
// import Divider from "components/atoms/Divider";
// import GoogleButton from "components/molecules/GoogleButton";
import ErrorMessage from "components/molecules/ErrorMessage";
import { LoginFormProps } from "types/organisms.types";
import { LoginFormData } from "types/auth.types";

interface FormErrors {
  email?: string;
  password?: string;
}

const LoginForm: React.FC<LoginFormProps> = ({
  onLogin,
  // onGoogleLogin,
  onForgotPassword,
  onSignUp,
  loading = false,
  error = null,
  // googleEnabled = true,
}) => {
  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
    keepLoggedIn: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showError, setShowError] = useState<boolean>(true);
  const [pwVisible, setPwVisible] = useState<boolean>(false);

  const normalizedError = useMemo(() => {
    if (!error) return null;
    if (typeof error === "string") return { kind: "unknown", message: error };
    return error;
  }, [error]);

  useEffect(() => {
    if (normalizedError) setShowError(true);
  }, [normalizedError]);

  const handleChange = (field: keyof LoginFormData, value: string | boolean): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
    if (showError) setShowError(false);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Please enter a valid email address";
    if (!formData.password.trim()) newErrors.password = "Password is required";
    else if (formData.password.length < 6)
      newErrors.password = "Password must be at least 6 characters";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (loading) return;
    setShowError(true);
    if (validateForm() && onLogin) onLogin(formData);
  };

  // const handleGoogleClick = (): void => {
  //   if (loading || !googleEnabled) return;
  //   setShowError(true);
  //   onGoogleLogin?.();
  // };

  const handleForgotPasswordClick = (e: React.MouseEvent): void => {
    e.preventDefault();
    onForgotPassword?.(formData.email || undefined);
  };

  const handleSignUpClick = (e: React.MouseEvent): void => {
    e.preventDefault();
    onSignUp?.();
  };

  return (
    <div className="w-full max-w-md space-y-6">
      {normalizedError && showError && (
        <ErrorMessage
          error={
            normalizedError.kind === "auth"
              ? "Invalid email or password."
              : normalizedError.message
          }
          onDismiss={() => setShowError(false)}
          className="mb-4"
        />
      )}

      {/* <GoogleButton onClick={handleGoogleClick} disabled={loading || !googleEnabled} /> */}

      {/* <Divider>or</Divider> */}

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <Input
          type="email"
          placeholder="Email Address"
          value={formData.email}
          onChange={(e) => handleChange("email", e.target.value)}
          error={errors.email}
          disabled={loading}
          required
          autoComplete="username"
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? "email-error" : undefined}
        />

        <div className="relative">
          <Input
            type={pwVisible ? "text" : "password"}
            placeholder="Password"
            value={formData.password}
            onChange={(e) => handleChange("password", e.target.value)}
            error={errors.password}
            disabled={loading}
            required
            autoComplete="current-password"
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? "password-error" : undefined}
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 hover:text-gray-700"
            onClick={() => setPwVisible((v) => !v)}
            aria-label={pwVisible ? "Hide password" : "Show password"}
            disabled={loading}
          >
            {pwVisible ? "Hide" : "Show"}
          </button>
        </div>

        <div className="flex items-center justify-between">
          <Checkbox
            label="Keep me logged in?"
            checked={formData.keepLoggedIn}
            onChange={(e) => handleChange("keepLoggedIn", e.target.checked)}
            disabled={loading}
          />
          <Link
            onClick={handleForgotPasswordClick}
            variant="muted"
            className="text-sm hover:text-teal-600 transition-colors duration-200"
          >
            Forgot password?
          </Link>
        </div>

        <Button
          type="submit"
          variant="teal"
          className="w-full py-3 font-semibold"
          loading={loading}
          disabled={loading}
        >
          {loading ? "Signing in..." : "Sign in"}
        </Button>
      </form>

      <div className="text-center text-sm text-gray-600">
        Don&apos;t have an account?{" "}
        <Link
          onClick={handleSignUpClick}
          className="font-medium text-teal-600 hover:text-teal-700 transition-colors duration-200"
        >
          Sign up
        </Link>
      </div>
    </div>
  );
};

export default LoginForm;
